import axiosInstance from '../helpers/axios';
import { format2Date, getTodayDateMoment } from '../helpers/hours';
import { handleError, handleInfo, handleSuccess } from '../helpers/utils';
import { getToken } from './auth';

// Register order
export const registerOrder = async (data) => {
	const token = getToken();
	return await axiosInstance({
		method: 'post',
		url: '/api/order/create/',
		headers: { Authorization: 'Bearer ' + token },
		data,
	});
};

// Update order
export const updateOrder = async (id, data) => {
	const token = getToken();
	axiosInstance({
		method: 'put',
		url: `/api/order/update/${id}`,
		headers: { Authorization: 'Bearer ' + token },
		data,
	});
};

// Update order's status
export const updateOrderStatus = async (order, status, wasRejected = false) => {
	const token = getToken();
	return await axiosInstance({
		method: 'put',
		url: `/api/order/update/${order.id}`,
		headers: { Authorization: 'Bearer ' + token },
		data: {
			...order,
			client_id: order.client_id.id,
			created_by: order.created_by.id,
			validation_date:
				status === 'VAL'
					? getTodayDateMoment()
					: order?.validation_date,
			released_date:
				status === 'REL' ? getTodayDateMoment() : order?.released_date,
			status,
			rejected: wasRejected,
			contacts: order.contacts.map((item) => item.id),
			// If it's "In configuration" or "Validated"
			// it can't be set as "released"
			released: !['ICF', 'VAL'].includes(status) ? 1 : 0,
		},
	});
};

// Update order's import
export const updateOrderImport = async (orderId, amount) => {
	const token = getToken();
	return await axiosInstance({
		method: 'post',
		url: '/api/order/update-order-import',
		headers: { Authorization: 'Bearer ' + token },
		data: {
			order_id: orderId,
			amount: amount,
		},
	});
};

// Update order's delivery forecast
export const updateOrderDeliveryForecast = async (
	order,
	newDeliveryForecast
) => {
	const token = getToken();
	return await axiosInstance({
		method: 'put',
		url: `/api/order/update/${order.id}`,
		headers: { Authorization: 'Bearer ' + token },
		data: {
			...order,
			client_id: order.client_id.id,
			created_by: order.created_by.id,
			contacts: order.contacts.map((item) => item.id),
			delivery_forecast: newDeliveryForecast,
		},
	});
};

// Convert quotation to and order
export const convertQuotationToOrder = async (orderId) => {
	const token = getToken();
	return await axiosInstance({
		method: 'post',
		url: '/api/order/convert-quotation-to-order',
		headers: { Authorization: 'Bearer ' + token },
		data: { order_id: orderId },
	});
};

// Get order info
export const getOrderInfo = async (id) => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/${id}`,
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Get all orders
export const getOrders = async (endpoint = null) => {
	const defaultEndpoint = `/api/order/?ordering=-created_at`; //&canceled=0';
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: endpoint ? endpoint : defaultEndpoint,
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Get all orders
export const getOrdersByStatus = async (status) => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/?ordering=-created_at&status=${status}&canceled=0`,
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Generate excel orders
export const generateExcelOrders = async (
	currentEndpoint,
	firstDate = null,
	secondDate = null
) => {
	// Feedback to the user
	handleInfo('Generando Excel...', 6);

	// Get token
	const token = getToken();

	// Filename
	let filename = 'RESUMEN_DE_PEDIDOS.xlsx';

	// Set default endpoint
	let endpoint = currentEndpoint;

	// Prepare and format dates
	const date1 = format2Date(firstDate);
	const date2 = format2Date(secondDate);

	// Prepare and format endpoint
	endpoint = currentEndpoint?.split('/?');
	const dateRangeParams =
		firstDate && secondDate
			? `created_at__gte=${date1}T00:00:00Z&created_at__lt=${date2}T23:59:59Z`
			: '';
	endpoint = `${endpoint[0]}/excel?${endpoint[1]}&${dateRangeParams}`;

	if (firstDate && secondDate) {
		// Set new filename
		filename = `PEDIDOS_PERIODO__${date1.replace(
			/-/g,
			'_'
		)}__${date2.replace(/-/g, '_')}.xlsx`;
	}

	return await axiosInstance({
		url: endpoint,
		headers: { Authorization: 'Bearer ' + token },
		method: 'get',
		responseType: 'blob',
	})
		.then((response) => {
			try {
				// URL
				const url = window.URL.createObjectURL(
					new Blob([response.data])
				);

				// Create link
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', filename);

				// Set link
				document.body.appendChild(link);
				link.click();
				handleSuccess('Reporte generado');
			} catch (error) {
				handleError(
					error,
					'Hubo un error al crear el reporte de los pedidos [1]'
				);
			}
		})
		.catch((error) => {
			handleError(
				error,
				'Hubo un error al crear el reporte de los pedidos [2]'
			);
		});
};

// Get all orders by number order
export const getOrdersByNumberOrder = async (data) => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/?search=${String(data.key).toUpperCase()}`,
		headers: { Authorization: 'Bearer ' + token },
	});
};

//
// Get order with biases
export const getOrdersWithBiases = async (numberOrder) => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/get-order-with-biases?number_order=${numberOrder.toUpperCase()}`,
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Get 5 latest orders
export const get5LatestOrders = async () => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: '/api/order/last-orders',
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Update stats
export const updateStats = async (
	month = null,
	year = new Date().getFullYear()
) => {
	if (!month) month = new Date().getMonth() + 1;
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/latest-stats?month=${month}&year=${year}`,
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Delete order
export const deleteOrder = (id) => {
	return () => {
		// Get token if authenticated
		const token = getToken();
		axiosInstance({
			method: 'delete',
			url: `/api/order/${id}`,
			headers: { Authorization: 'Bearer ' + token },
		})
			.then(() => {
				handleSuccess('Pedido eliminado correctamente.');
			})
			.catch((error) => {
				handleError(error, 'No se pudo eliminar el pedido.');
			});
	};
};

// Cancel order
export const cancelOrder = async (order) => {
	const token = getToken();
	return await axiosInstance({
		method: 'put',
		url: `/api/order/update/${order.id}`,
		headers: { Authorization: 'Bearer ' + token },
		data: {
			...order,
			client_id: order.client_id,
			created_by: order.created_by.id,
			contacts: order.contacts.map((item) => item.id),
			canceled: true,
		},
	});
};

// Status count dashboard
export const getStatusCounts = async () => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: '/api/order/status-counts-dashboard',
		headers: { Authorization: 'Bearer ' + token },
	});
};

// Update validation date
export const updateValidationDate = async (data) => {
	const token = getToken();
	return await axiosInstance({
		method: 'post',
		url: '/api/order/update-validation-date',
		headers: { Authorization: 'Bearer ' + token },
		data,
	});
};

// Create suborder
export const registerSubOrder = async (data) => {
	const token = getToken();
	return await axiosInstance({
		method: 'post',
		url: '/api/order/create-suborder',
		headers: { Authorization: 'Bearer ' + token },
		data,
	});
};

// Get tracking order
export const getTrackingOrder = async (orderId) => {
	const token = getToken();
	return await axiosInstance({
		method: 'get',
		url: `/api/order/tracking-order-client/${orderId}`,
		headers: { Authorization: 'Bearer ' + token },
	});
};
